// src/hooks/useFootprintOverlay.js
//
// The entitled mask and edges on one MapLibre map (footprint restyle S-b):
// footprintStyle.js's layers under `anchor`, kept in step with an answer of
// useEntitledFootprints. Explore calls it for each side of the compare view;
// PadPlannerMap for its one map.
//
//   role, tone   decide the opacity (footprintStyle.maskOpacity); a role that
//                draws no mask (dashboard, paper) takes the layers off
//   entitled     {mask, features}: the world masked until an answer
//   visible      false hides every layer (Explore's purchase mode)
//   selectedKeys the selected captures' dataset_keys (S-c1): T3/T4 and the
//                hatch, where the role draws them; [] for none. Topmost first:
//                the order the chips are kept in
//   clipX        Compare: () => [left, right], this map's side of the slider
//                in its px; clipKey changes when it does (S-c2)
//
// THE CHIP (S-c2): lib/map-overlay/CaptureChips.js over the map, one per
// selected capture with an entitled feature, below that capture's minzoom,
// where the role draws the selection. Hidden with the layers (visible).
//
// The hatch is a map image: a style reload drops it, and the map's
// styleimagemissing event puts it back.
//
// A map removed under it (Explore's compare map on leaving compare, REM-703)
// is never called: the map is dropped on its "remove" event, and each effect
// asks whether it is still live when it runs, since the caller's remove can
// run in the same commit, after this hook was handed the map at render.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  drawsSelection, ensureFootprintStyle, ensureHatchImage, removeFootprintStyle, setFootprintData, setFootprintVisible, setSelectedKeys,
  FP_HATCH_IMAGE, WORLD_MASKED,
} from "../lib/map/footprintStyle";
import { isLiveMap } from "../lib/map/liveMap";
import CaptureChips, { captureChipItems } from "../lib/map-overlay/CaptureChips";

const NONE = Object.freeze([]);

export function useFootprintOverlay({
  map: given, anchor = null, role, tone = "dark", entitled = WORLD_MASKED, visible = true, selectedKeys = NONE, clipX = null, clipKey = null,
}) {
  // The map this hook let go of on its remove event.
  const [dropped, setDropped] = useState(null);
  const map = isLiveMap(given) && given !== dropped ? given : null;
  const shown = useRef(false);
  // What a (re)made layer set starts from; kept current before the effects below run.
  const latest = useRef({ entitled, visible, selectedKeys, clipX });
  useLayoutEffect(() => { latest.current = { entitled, visible, selectedKeys, clipX }; });
  // An equal list from a new array is no change.
  const keysId = JSON.stringify(selectedKeys ?? []);

  useEffect(() => {
    if (!isLiveMap(map)) return undefined;
    const drop = () => setDropped(map);
    const imageMissing = (e) => { if (e?.id === FP_HATCH_IMAGE && isLiveMap(map)) ensureHatchImage(map); };
    map.on("remove", drop);
    map.on("styleimagemissing", imageMissing);
    return () => {
      if (!isLiveMap(map)) return;
      map.off("remove", drop);
      map.off("styleimagemissing", imageMissing);
    };
  }, [map]);

  useEffect(() => {
    if (!isLiveMap(map)) return;
    shown.current = ensureFootprintStyle(map, anchor, { role, tone, entitled: latest.current.entitled, visible: latest.current.visible });
  }, [map, anchor, role, tone]);

  useEffect(() => {
    if (isLiveMap(map) && shown.current) setFootprintData(map, entitled);
  }, [map, entitled, role, tone]);

  useEffect(() => {
    if (isLiveMap(map) && shown.current) setFootprintVisible(map, visible);
  }, [map, visible, role, tone]);

  useEffect(() => {
    if (isLiveMap(map) && shown.current) setSelectedKeys(map, latest.current.selectedKeys);
  }, [map, keysId, role, tone]);

  // The chips: made per map and role, fed on each change of what they show.
  const chips = useRef(null);
  useEffect(() => {
    if (!isLiveMap(map) || !drawsSelection(role)) return undefined;
    const c = new CaptureChips({ map, clipX: () => latest.current.clipX?.() ?? null });
    chips.current = c;
    return () => { chips.current = null; c.destroy(); };
  }, [map, role]);

  useEffect(() => {
    if (!isLiveMap(map)) return;
    const { entitled: e, selectedKeys: keys, visible: on } = latest.current;
    chips.current?.set(captureChipItems(e?.features, keys), { visible: on });
  }, [map, role, entitled, keysId, visible]);

  useEffect(() => { if (isLiveMap(map)) chips.current?.place(); }, [map, clipKey]);

  // Off the map when the caller goes; a removed map has no style to touch.
  useEffect(() => () => {
    if (isLiveMap(map)) removeFootprintStyle(map);
  }, [map]);
}
